// todo: Implement table to list all the contacts

import { trpc } from '@web/lib/trpc';
import ContactTable from './ContactTable';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Filter } from '@web/components/ui/view-filters';

import { ViewContainer } from '@web/components/ui/view-container';

import { EditContactDialog } from '@web/routes/dashboard/chat/-components/edit-contact-dialog';

import { Route } from '@web/routes/dashboard/admin/contact/route';
import { CreateContactDialog } from '@web/routes/dashboard/chat/-components/create-contact-dialog';

export function ContactTableView() {
  const {
    editContactDialogContactId,
    setEditContactDialogContactId,
    setCreateContactDialogOpen,
    createContactDialogOpen,
  } = Route.useRouteContext();

  const { getContacts: getContactsUtils } = trpc.useUtils().messaging;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [totalPages, setTotalPages] = useState(1);

  const navigate = Route.useNavigate();

  const [_isLoading, setIsLoading] = useState(false);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const {
    firstName,
    lastName,
    phoneNumber,
    email,
    hasPayments,
    hasSignedContracts,
    oldSystemId,
  } = Route.useSearch();

  const {
    data: contactsData,
    refetch: refetchContactsData,
    isLoading,
    error,
  } = trpc.contact.listAllContacts.useQuery({
    page,
    pageSize,
    filters: {
      firstName,
      lastName,
      phoneNumber,
      email,
      hasPayments,
      hasSignedContracts,
      oldSystemId,
    },
  });

  // Update total pages when data changes
  useEffect(() => {
    if (contactsData?.total) {
      setTotalPages(Math.ceil(contactsData.total / pageSize));
    }
  }, [contactsData, pageSize]);

  useEffect(() => {
    if (error) {
      toast.error('Error fetching contacts');
    }
  }, [error]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  const filters = [
    {
      key: 'firstName',
      label: 'First Name',
      type: 'string',
      value: firstName,
      isMainFilter: true,
    },
    {
      key: 'lastName',
      label: 'Last Name',
      type: 'string',
      value: lastName,
      isMainFilter: true,
    },
    {
      key: 'phoneNumber',
      label: 'Phone Number',
      type: 'string',
      value: phoneNumber,
      isMainFilter: true,
    },

    { key: 'email', label: 'Email', type: 'string', value: email },

    {
      key: 'oldSystemId',
      label: 'Old System ID',
      type: 'number',
      value: oldSystemId,
      isMainFilter: false,
    },

    {
      key: 'hasSignedContracts',
      label: 'Has Signed Contracts',
      type: 'boolean',
      value: hasSignedContracts,
    },
    {
      key: 'hasPayments',
      label: 'Has Payments',
      type: 'boolean',
      value: hasPayments,
    },
  ] as const satisfies Filter[];

  const onFilterChange = (
    newSearch: Record<string, string | number | boolean | object | undefined>,
  ) => {
    console.log(`Updaing filteres: `, newSearch);

    setPage(1);
    void navigate({
      search: (current: URLSearchParams) => {
        Object.entries(newSearch).forEach(([key, value]) => {
          if (value !== undefined) {
            if (typeof value === 'object') {
              console.warn(
                `Value for key "${key}" is an object and cannot be converted to a string.`,
              );
            } else {
              current.set(key, value.toString());
            }
          } else {
            current.delete(key);
          }
        });
        return current;
      },
    });
  };

  const allContacts = useMemo(() => {
    return contactsData?.contacts ?? [];
  }, [contactsData]);

  return (
    <ViewContainer
      title="Contacts"
      addButtonLabel="Add Contact"
      filters={filters}
      onFilterChange={onFilterChange}
      onAddButtonClick={() => {
        setCreateContactDialogOpen(true);
      }}
    >
      {/* Your table or any other content goes here */}
      <ContactTable
        contacts={allContacts}
        pagination={{
          currentPage: page,
          totalPages: totalPages,
          totalItems: contactsData?.total ?? 0,
          pageSize: pageSize,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        isLoading={_isLoading}
      />

      {editContactDialogContactId !== null && (
        <EditContactDialog
          key={editContactDialogContactId}
          contactId={editContactDialogContactId}
          onClose={() => {
            setEditContactDialogContactId(null);
            void refetchContactsData();
          }}
        />
      )}

      {createContactDialogOpen && (
        <CreateContactDialog
          onClose={() => {
            setCreateContactDialogOpen(false);
            void getContactsUtils.refetch();
          }}
        />
      )}
    </ViewContainer>
  );
}
